import type { Transition, Variants } from "framer-motion";

/**
 * Shared motion vocabulary. Everything is short, eased out, and mostly opacity
 * plus small translation — motion should make hierarchy legible, not perform.
 * `prefers-reduced-motion` is honoured globally in theme.css.
 */

export const easeOutExpo = [0.16, 1, 0.3, 1] as const;
export const easeOutQuart = [0.25, 1, 0.5, 1] as const;

export const springSoft: Transition = {
  type: "spring",
  stiffness: 320,
  damping: 30,
  mass: 0.7,
};

export const springSnappy: Transition = {
  type: "spring",
  stiffness: 520,
  damping: 32,
  mass: 0.6,
};

export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.25, ease: easeOutQuart } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

export const fadeUp: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: easeOutExpo } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.18 } },
};

export const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.96, y: 8 },
  visible: { opacity: 1, scale: 1, y: 0, transition: springSoft },
  exit: { opacity: 0, scale: 0.97, y: 4, transition: { duration: 0.14 } },
};

export const slideOverRight: Variants = {
  hidden: { x: "100%" },
  visible: { x: 0, transition: { duration: 0.35, ease: easeOutExpo } },
  exit: { x: "100%", transition: { duration: 0.25, ease: easeOutQuart } },
};

/** Parent wrapper that staggers its children in. */
export function staggerChildren(stagger = 0.05, delay = 0): Variants {
  return {
    hidden: {},
    visible: {
      transition: { staggerChildren: stagger, delayChildren: delay },
    },
  };
}

/** Rows in a table or list. Kept subtle so a 25-row page does not ripple. */
export const listItem: Variants = {
  hidden: { opacity: 0, y: 6 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.28, ease: easeOutQuart } },
};
